import React, { useState, useEffect } from 'react';
import { getData, saveData, db } from '../firebase';
import { doc, deleteDoc } from 'firebase/firestore';

// Reusable register for Petty Cash, Cash Book, Bank Book
export default function CashRegister({ user, collectionName, title, subtitle, inLabel, outLabel, inType, outType, accent }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ type: inType, description: '', amount: '', date: new Date().toISOString().slice(0, 10) });

  const load = async () => {
    setLoading(true);
    const data = await getData(user.uid, collectionName);
    setItems(data);
    setLoading(false);
  };

  useEffect(() => { load(); }, [collectionName]);

  const add = async (e) => {
    e.preventDefault();
    if (!form.description || !form.amount) return;
    await saveData(user.uid, collectionName, { ...form, amount: parseFloat(form.amount) || 0 });
    setShowForm(false);
    setForm({ type: inType, description: '', amount: '', date: new Date().toISOString().slice(0, 10) });
    load();
  };

  const del = async (id) => {
    if (!window.confirm('Delete this entry?')) return;
    await deleteDoc(doc(db, 'users', user.uid, collectionName, id));
    load();
  };

  const fmt = n => '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const totalIn = items.filter(i => i.type === inType).reduce((s, i) => s + i.amount, 0);
  const totalOut = items.filter(i => i.type === outType).reduce((s, i) => s + i.amount, 0);
  const balance = totalIn - totalOut;
  const sorted = [...items].sort((a, b) => (b.date || '').localeCompare(a.date || ''));

  if (loading) return <div style={{ textAlign: 'center', padding: 60, color: '#aaa' }}>Loading...</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <h2 style={{ fontSize: 22, fontWeight: 700 }}>{title}</h2>
        <button onClick={() => setShowForm(true)} style={{ padding: '10px 20px', background: accent, color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>+ New Entry</button>
      </div>
      <div style={{ fontSize: 13, color: '#888', marginBottom: 20 }}>{subtitle}</div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 20 }}>
        <div style={{ background: '#e1f5ee', borderRadius: 12, padding: 18 }}>
          <div style={{ fontSize: 12, color: '#0f6e56', marginBottom: 4, textTransform: 'uppercase' }}>{inLabel}</div>
          <div style={{ fontSize: 24, fontWeight: 700, color: '#0f6e56' }}>{fmt(totalIn)}</div>
        </div>
        <div style={{ background: '#fcebeb', borderRadius: 12, padding: 18 }}>
          <div style={{ fontSize: 12, color: '#a32d2d', marginBottom: 4, textTransform: 'uppercase' }}>{outLabel}</div>
          <div style={{ fontSize: 24, fontWeight: 700, color: '#a32d2d' }}>{fmt(totalOut)}</div>
        </div>
        <div style={{ background: balance >= 0 ? '#e6f1fb' : '#faeeda', borderRadius: 12, padding: 18 }}>
          <div style={{ fontSize: 12, color: balance >= 0 ? '#185fa5' : '#854f0b', marginBottom: 4, textTransform: 'uppercase' }}>Balance</div>
          <div style={{ fontSize: 24, fontWeight: 700, color: balance >= 0 ? '#185fa5' : '#854f0b' }}>{fmt(balance)}</div>
        </div>
      </div>

      <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e8e8e8', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr style={{ background: '#f7f8fa' }}>
            {['Date', 'Description', 'Type', 'Amount', ''].map(h => (
              <th key={h} style={{ padding: '11px 16px', textAlign: h === 'Amount' ? 'right' : 'left', fontSize: 11, color: '#888', fontWeight: 600, textTransform: 'uppercase' }}>{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {sorted.length === 0 ? (
              <tr><td colSpan={5} style={{ textAlign: 'center', padding: 50, color: '#aaa' }}>No entries yet. Click "+ New Entry" to start.</td></tr>
            ) : sorted.map(item => (
              <tr key={item.id} style={{ borderTop: '1px solid #f0f0f0' }}>
                <td style={td}>{item.date}</td>
                <td style={{ ...td, fontWeight: 500 }}>{item.description}</td>
                <td style={td}>
                  <span style={{ fontSize: 11, padding: '2px 10px', borderRadius: 10, fontWeight: 600, background: item.type === inType ? '#e1f5ee' : '#fcebeb', color: item.type === inType ? '#0f6e56' : '#a32d2d' }}>
                    {item.type === inType ? inLabel : outLabel}
                  </span>
                </td>
                <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: item.type === inType ? '#0f6e56' : '#a32d2d' }}>
                  {item.type === inType ? '+' : '-'}{fmt(item.amount)}
                </td>
                <td style={{ ...td, textAlign: 'right' }}>
                  <button onClick={() => del(item.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#e24b4a', fontSize: 15 }}>🗑</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 20 }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 32, width: '100%', maxWidth: 420 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
              <div style={{ fontSize: 18, fontWeight: 700 }}>New {title} Entry</div>
              <button onClick={() => setShowForm(false)} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#888' }}>×</button>
            </div>
            <form onSubmit={add}>
              <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
                <button type="button" onClick={() => setForm(f => ({ ...f, type: inType }))} style={{ flex: 1, padding: 12, borderRadius: 10, border: `1.5px solid ${form.type === inType ? '#0f6e56' : '#ddd'}`, background: form.type === inType ? '#0f6e56' : '#fff', color: form.type === inType ? '#fff' : '#555', cursor: 'pointer', fontWeight: 600 }}>{inLabel}</button>
                <button type="button" onClick={() => setForm(f => ({ ...f, type: outType }))} style={{ flex: 1, padding: 12, borderRadius: 10, border: `1.5px solid ${form.type === outType ? '#a32d2d' : '#ddd'}`, background: form.type === outType ? '#a32d2d' : '#fff', color: form.type === outType ? '#fff' : '#555', cursor: 'pointer', fontWeight: 600 }}>{outLabel}</button>
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={lbl}>Description *</label>
                <input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="e.g. Daily sales deposit" style={inp} />
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={lbl}>Amount *</label>
                <input type="number" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} placeholder="0.00" style={inp} />
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={lbl}>Date</label>
                <input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} style={inp} />
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button type="button" onClick={() => setShowForm(false)} style={{ flex: 1, padding: 12, borderRadius: 10, border: '1px solid #ddd', background: '#fff', cursor: 'pointer' }}>Cancel</button>
                <button type="submit" style={{ flex: 2, padding: 12, background: accent, color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, cursor: 'pointer' }}>Save Entry</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const td = { padding: '12px 16px', fontSize: 13, color: '#444' };
const lbl = { display: 'block', fontSize: 13, fontWeight: 500, color: '#555', marginBottom: 6 };
const inp = { width: '100%', padding: '10px 14px', border: '1px solid #ddd', borderRadius: 10, fontSize: 14, color: '#222', outline: 'none', background: '#fff' };
